import React, { useState, useEffect, useRef, Fragment } from "react";
import { PORTFOLIO as PD } from "./data";
import { useInView } from "./hooks";
import { ManifestoPortrait } from "./Portrait";

export function Manifesto({ t }) {
  const [ref, seen] = useInView();
  return (
    <section id="manifesto" className={"d3-mani reveal " + (seen ? "is-in" : "")} ref={ref}>
      <div className="d3-eyebrow"><span>{t(PD.i18n.mani_eyebrow)}</span><span className="d3-rule" /></div>
      <div className="d3-mani-grid">
        <h2 className="d3-mani-h">{t(PD.i18n.mani_h)}</h2>
        <div className="d3-mani-body">
          <p>{t(PD.i18n.mani_p1)}</p>
          <p><b>{t(PD.i18n.mani_p2)}</b></p>
          <p>{t(PD.i18n.mani_p3)}</p>
        </div>
        <div className="d3-mani-portrait">
          <ManifestoPortrait caption="HI3RIS" />
        </div>
      </div>
    </section>
  );
}

export function WorksSection({ t, lang, onOpen, projects }) {
  const [filter, setFilter] = useState("all");
  const [ref, seen] = useInView();
  const tags = ["all", ...Object.keys(PD.tags)];
  const list = projects || PD.projects;
  const filtered = filter === "all" ? list : list.filter((p) => p.tag === filter);
  const counts = list.reduce((a, p) => { a[p.tag] = (a[p.tag] || 0) + 1; return a; }, {});
  return (
    <section id="works" className={"d3-works reveal " + (seen ? "is-in" : "")} ref={ref} aria-labelledby="works-h">
      <div className="d3-eyebrow"><span>{t(PD.i18n.works_eyebrow)}</span><span className="d3-rule" /></div>
      <h2 id="works-h" className="d3-section-h">{t(PD.i18n.works_h).split("\n").map((s,i)=><Fragment key={i}>{s}{i===0 && <br/>}</Fragment>)}</h2>

      <div className="filter-bar" role="group" aria-label="Filter projects by tag">
        {tags.map((tg) => (
          <button
            key={tg}
            type="button"
            aria-pressed={filter === tg}
            className={"filter-chip" + (filter === tg ? " is-on" : "")}
            onClick={() => setFilter(tg)}
          >
            <span>{tg === "all" ? t(PD.i18n.filter_all) : t(PD.tags[tg])}</span>
            <span className="filter-count">{tg === "all" ? list.length : (counts[tg] || 0)}</span>
          </button>
        ))}
      </div>

      <div className="d3-grid">
        {filtered.map((p) => (
          <article
            key={p.id}
            className={"d3-card d3-card-" + p.tag}
            tabIndex="0"
            role="button"
            aria-label={`Open case file: ${p.name}`}
            onClick={() => onOpen(p)}
            onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onOpen(p); } }}
          >
            <div className="d3-card-hd">
              <span className="d3-card-sig">{p.sig}</span>
              <span className="d3-card-tag">{t(PD.tags[p.tag] || { fr: p.tag, en: p.tag }).toUpperCase()}</span>
              <span className="d3-card-yr">{p.year}</span>
            </div>
            <div className="d3-card-name">{p.name}</div>
            <div className="d3-card-kind">{lang === "fr" ? p.kind_fr : p.kind_en}</div>
            <p className="d3-card-blurb">{t(p.blurb)}</p>
            <div className="d3-card-stack">
              {p.stack.map((s) => <span key={s}>· {s}</span>)}
            </div>
            <div className="d3-card-foot">
              <span>{t(PD.i18n.open_packet)}</span>
              <span className="d3-card-arrow" aria-hidden="true">⟶</span>
            </div>
          </article>
        ))}
      </div>
    </section>
  );
}

export function CaseFile({ project, onClose, t, lang }) {
  const panelRef = useRef(null);
  const closeRef = useRef(null);
  useEffect(() => {
    if (!project) return;
    // Remember what opened the dialog, move focus inside, trap Tab, restore on close.
    const opener = document.activeElement;
    if (closeRef.current) closeRef.current.focus();
    const onKey = (e) => {
      if (e.key === "Escape") { onClose(); return; }
      if (e.key !== "Tab" || !panelRef.current) return;
      const focusables = panelRef.current.querySelectorAll(
        'a[href], button:not([disabled]), input, textarea, select, [tabindex]:not([tabindex="-1"])'
      );
      if (!focusables.length) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    };
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
      if (opener && typeof opener.focus === "function") opener.focus();
    };
  }, [project, onClose]);
  if (!project) return null;
  const p = project;
  return (
    <div className="case-overlay" role="dialog" aria-modal="true" aria-labelledby="case-h" onClick={onClose}>
      <div className="case-file" ref={panelRef} onClick={(e) => e.stopPropagation()}>
        <header className="case-hd">
          <div className="case-hd-l">
            <span className="case-sig">{p.sig}</span>
            <span className="case-tag">{t(PD.tags[p.tag] || {fr:p.tag,en:p.tag}).toUpperCase()}</span>
            <span className="case-year">{p.year}</span>
          </div>
          <button ref={closeRef} className="case-close" onClick={onClose} aria-label={t(PD.i18n.case_close)}>
            <span aria-hidden="true">×</span>
            <span className="case-close-l">{t(PD.i18n.case_close)} · ESC</span>
          </button>
        </header>

        <div className="case-body">
          <h3 id="case-h" className="case-name">{p.name}</h3>
          <div className="case-kind">{lang === "fr" ? p.kind_fr : p.kind_en}</div>

          <div className="case-meta">
            <div><div className="cm-l">{t(PD.i18n.case_role)}</div><div>{t(p.role)}</div></div>
            <div><div className="cm-l">{t(PD.i18n.case_year)}</div><div>{p.year}</div></div>
            <div className="case-stack-cell"><div className="cm-l">{t(PD.i18n.case_stack)}</div>
              <div className="case-stack">{p.stack.map((s) => <span key={s} className="d3-tag">{s}</span>)}</div>
            </div>
          </div>

          <section className="case-section">
            <div className="case-eyebrow">{t(PD.i18n.case_context)}</div>
            <p>{t(p.context)}</p>
          </section>
          <section className="case-section">
            <div className="case-eyebrow">{t(PD.i18n.case_what)}</div>
            <p>{t(p.what)}</p>
          </section>
          <section className="case-section">
            <div className="case-eyebrow">{t(PD.i18n.case_outcome)}</div>
            <p>{t(p.outcome)}</p>
          </section>
        </div>
      </div>
    </div>
  );
}

export function SkillsSection({ t }) {
  const [ref, seen] = useInView();
  return (
    <section id="skills" className={"d3-skills-sec reveal " + (seen ? "is-in" : "")} ref={ref}>
      <div className="d3-eyebrow"><span>{t(PD.i18n.skills_eyebrow)}</span><span className="d3-rule" /></div>
      <h2 className="d3-section-h">{t(PD.i18n.skills_h)}</h2>
      <div className="d3-skills">
        {PD.skills.map((g, i) => (
          <div key={i} className="d3-skill">
            <div className="d3-skill-hd">
              <span className="d3-skill-i">{String(i+1).padStart(2,"0")}</span>
              {g.glyph && <span className="d3-skill-glyph" aria-hidden="true">{g.glyph}</span>}
            </div>
            <div className="d3-skill-row">
              <div className="d3-skill-h">{t(g.group)}</div>
              <span className="d3-skill-count">{String(g.items.length).padStart(2,"0")} TOOLS</span>
            </div>
            {g.focus && <div className="d3-skill-focus">{t(g.focus)}</div>}
            <div className="d3-skill-list">
              {g.items.map((it) => <span key={it} className="d3-tag">{it}</span>)}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

export function TimelineSection({ t }) {
  const [ref, seen] = useInView();
  return (
    <section id="record" className={"d3-rec reveal " + (seen ? "is-in" : "")} ref={ref}>
      <div className="d3-eyebrow"><span>{t(PD.i18n.record_eyebrow)}</span><span className="d3-rule" /></div>
      <h2 className="d3-section-h">{t(PD.i18n.record_h).split("\n").map((s,i)=><Fragment key={i}>{s}{i===0 && <br/>}</Fragment>)}</h2>
      <div className="d3-tl">
        {PD.experience.map((e, i) => (
          <div key={i} className="d3-tl-row">
            <div className="d3-tl-d">{e.date}</div>
            <div className="d3-tl-x" aria-hidden="true">×</div>
            <div>
              <div className="d3-tl-r">{t(e.role)}</div>
              <div className="d3-tl-o">{t(e.org)}</div>
              <div className="d3-tl-n">{t(e.note)}</div>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

export function VoicesSection({ t }) {
  const [ref, seen] = useInView();
  return (
    <section className={"d3-voices reveal " + (seen ? "is-in" : "")} ref={ref}>
      <div className="d3-eyebrow"><span>{t(PD.i18n.voices_eyebrow)}</span><span className="d3-rule" /></div>
      <div className="d3-voices-grid">
        {PD.testimonials.map((q, i) => (
          <figure key={i} className="d3-voice">
            <div className="d3-voice-i">{String(i+1).padStart(2,"0")}</div>
            <blockquote>"{t(q)}"</blockquote>
            <figcaption>— {t(q.who)}</figcaption>
          </figure>
        ))}
      </div>
    </section>
  );
}

export function WritingSection({ t, articles }) {
  const [ref, seen] = useInView();
  const list = articles && articles.length ? articles : PD.writing;
  return (
    <section id="writing" className={"d3-writing-sec reveal " + (seen ? "is-in" : "")} ref={ref}>
      <div className="d3-eyebrow"><span>{t(PD.i18n.writing_eyebrow)}</span><span className="d3-rule" /></div>
      <h2 className="d3-section-h">{t(PD.i18n.writing_h).split("\n").map((s,i)=><Fragment key={i}>{s}{i===0 && <br/>}</Fragment>)}</h2>
      <div className="d3-writing">
        {list.map((w, i) => (
          <a key={i} className="d3-write" href={w.url || "#"} target={w.url ? "_blank" : undefined} rel="noreferrer">
            <span className="d3-write-i">{String(i+1).padStart(2,"0")}</span>
            <span className="d3-write-d">{w.date}</span>
            <span className="d3-write-t">{t(w.title)}</span>
            <span className="d3-write-r">{w.read} ⟶</span>
          </a>
        ))}
      </div>
    </section>
  );
}

const encodeForm = (data) => new URLSearchParams(data).toString();

export function ContactSection({ t }) {
  const [ref, seen] = useInView();
  const [status, setStatus] = useState("idle"); // idle | sending | sent | error
  const [errs, setErrs] = useState({});
  const onSubmit = async (e) => {
    e.preventDefault();
    const form = e.target;
    const f = new FormData(form);
    const errors = {};
    if (!f.get("from") || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(f.get("from"))) errors.from = true;
    if (!f.get("subject") || f.get("subject").trim().length < 3) errors.subject = true;
    if (!f.get("body") || f.get("body").trim().length < 10) errors.body = true;
    setErrs(errors);
    if (Object.keys(errors).length > 0) {
      const firstInvalid = form.querySelector(`[name="${Object.keys(errors)[0]}"]`);
      if (firstInvalid) firstInvalid.focus();
      return;
    }
    setStatus("sending");
    try {
      // Netlify Forms: the static twin of this form lives in public/index.html.
      const res = await fetch("/", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: encodeForm(f),
      });
      if (!res.ok) throw new Error("HTTP " + res.status);
      setStatus("sent");
      form.reset();
    } catch (err) {
      setStatus("error");
    }
  };
  const field = (name) => ({
    name,
    required: true,
    "aria-invalid": !!errs[name],
    "aria-describedby": errs[name] ? `contact-${name}-err` : undefined,
  });
  const fieldErr = (name, msg) => errs[name] && (
    <span id={`contact-${name}-err`} className="form-err">{t(msg)}</span>
  );
  const hasCv = PD.identity.cv_url && PD.identity.cv_url !== "#";
  return (
    <section id="contact" className={"d3-contact-sec reveal " + (seen ? "is-in" : "")} ref={ref}>
      <div className="d3-eyebrow"><span>{t(PD.i18n.contact_eyebrow)}</span><span className="d3-rule" /></div>
      <div className="d3-contact-grid">
        <div>
          <h2 className="d3-section-h">{t(PD.i18n.contact_h).split("\n").map((s,i)=><Fragment key={i}>{s}{i===0 && <br/>}</Fragment>)}</h2>
          <p className="d3-contact-p">{t(PD.i18n.contact_p)}</p>
          <ul className="d3-contact-list">
            <li><span>NAME</span><b>{PD.identity.legal_name} <span className="d3-mute">/</span> {PD.identity.name}</b></li>
            <li><span>EMAIL</span><a href={"mailto:" + PD.identity.email}><b>{PD.identity.email}</b></a></li>
            <li><span>WHATSAPP</span><a href={PD.identity.whatsapp_url} target="_blank" rel="noreferrer"><b>{PD.identity.whatsapp}</b></a></li>
            <li><span>GITHUB</span><a href={PD.identity.github_url} target="_blank" rel="noreferrer"><b>github.com/hi3ris</b></a></li>
            <li><span>X</span><a href={PD.identity.x_url} target="_blank" rel="noreferrer"><b>x.com/ramsesdagban</b></a></li>
            <li><span>DEV.TO</span><a href={PD.identity.devto_url} target="_blank" rel="noreferrer"><b>dev.to/hi3ris</b></a></li>
            <li><span>LINKEDIN</span><a href={PD.identity.linkedin_url} target="_blank" rel="noreferrer"><b>linkedin.com/in/hi3ris</b></a></li>
            <li><span>BASE</span><b>{PD.identity.location}</b></li>
          </ul>
          <div className="d3-contact-ctas">
            <a className="wa-button" href={PD.identity.whatsapp_url} target="_blank" rel="noreferrer">
              <span aria-hidden="true">↗</span> WHATSAPP {PD.identity.whatsapp}
            </a>
            {hasCv && (
              <a className="cv-button" href={PD.identity.cv_url} download>
                <span aria-hidden="true">↓</span> {t(PD.i18n.cv)}
              </a>
            )}
          </div>
        </div>
        <form className="d3-form" name="contact" method="POST" data-netlify="true"
              netlify-honeypot="bot-field" onSubmit={onSubmit} noValidate>
          <input type="hidden" name="form-name" value="contact" />
          <p className="d3-form-hp" aria-hidden="true">
            <label>Leave empty <input name="bot-field" tabIndex="-1" autoComplete="off" /></label>
          </p>
          <label>
            <span>{t(PD.i18n.form.from)}</span>
            <input type="email" autoComplete="email" {...field("from")}
                   placeholder={t(PD.i18n.form.placeholder_from)} />
            {fieldErr("from", PD.i18n.form.err_from)}
          </label>
          <label>
            <span>{t(PD.i18n.form.subject)}</span>
            <input type="text" {...field("subject")}
                   placeholder={t(PD.i18n.form.placeholder_subject)} />
            {fieldErr("subject", PD.i18n.form.err_subject)}
          </label>
          <label>
            <span>{t(PD.i18n.form.body)}</span>
            <textarea rows="6" {...field("body")} placeholder="..."></textarea>
            {fieldErr("body", PD.i18n.form.err_body)}
          </label>
          <button type="submit" disabled={status === "sending" || status === "sent"}>
            {status === "sending" ? t(PD.i18n.form.sending)
              : status === "sent" ? t(PD.i18n.form.sent)
              : t(PD.i18n.form.send)}
          </button>
          <p className="form-status" role="status" aria-live="polite">
            {status === "error" ? t(PD.i18n.form.failed) : ""}
          </p>
        </form>
      </div>
    </section>
  );
}

export function Foot({ t }) {
  return (
    <footer className="d3-foot" role="contentinfo">
      <div className="d3-foot-l"><b>HI3RIS</b> · MITM PORTFOLIO · {new Date().getFullYear()}</div>
      <div className="d3-foot-m">{t(PD.i18n.foot_quote)}</div>
      <a className="d3-foot-r" href="#top">{t(PD.i18n.back_top)}</a>
    </footer>
  );
}
